import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { dashboardAPI, sedesAPI } from '../../services/api';
import { BarChart2, Loader2, Download, TrendingUp, CalendarDays, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { format, startOfMonth, endOfMonth, subMonths, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const PRESET_RANGES = [
  { label: 'Esta semana', getRange: () => { const now = new Date(); return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) }; } },
  { label: 'Próxima semana', getRange: () => { const now = new Date(); const nextWeekStart = addDays(startOfWeek(now, { weekStartsOn: 1 }), 7); return { from: nextWeekStart, to: addDays(nextWeekStart, 6) }; } },
  { label: 'Semana pasada', getRange: () => { const now = new Date(); const lastWeekStart = addDays(startOfWeek(now, { weekStartsOn: 1 }), -7); return { from: lastWeekStart, to: addDays(lastWeekStart, 6) }; } },
  { label: 'Este mes', getRange: () => { const now = new Date(); return { from: startOfMonth(now), to: endOfMonth(now) }; } },
  { label: 'Mes pasado', getRange: () => { const now = new Date(); const prev = subMonths(now, 1); return { from: startOfMonth(prev), to: endOfMonth(prev) }; } },
  { label: 'Últimos 3 meses', getRange: () => { const now = new Date(); return { from: startOfMonth(subMonths(now, 2)), to: endOfMonth(now) }; } },
  { label: 'Todo', getRange: () => ({ from: null, to: null }) },
];

const ReportsPage = () => {
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState('');
  const [equityData, setEquityData] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) });
  const [calOpen, setCalOpen] = useState(false);
  const [activePreset, setActivePreset] = useState('Este mes');

  useEffect(() => {
    loadSedes();
  }, []);

  const loadReports = useCallback(async () => {
    if (!selectedSede) return;
    setLoading(true);
    try {
      const dateFrom = dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined;
      const dateTo = dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined;
      const [equity, heatmap] = await Promise.all([
        dashboardAPI.getEquityReport(selectedSede, dateFrom, dateTo),
        dashboardAPI.getHeatmap(selectedSede)
      ]);
      setEquityData(equity.data);
      setHeatmapData(heatmap.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedSede, dateRange]);

  useEffect(() => {
    if (selectedSede) {
      loadReports();
    }
  }, [selectedSede, loadReports]);

  const loadSedes = async () => {
    try {
      const response = await sedesAPI.getAll();
      setSedes(response.data);
      if (response.data.length > 0) {
        setSelectedSede(response.data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreset = (preset) => {
    const range = preset.getRange();
    setDateRange({ from: range.from, to: range.to });
    setActivePreset(preset.label);
    if (preset.label !== 'Todo') {
      setCalOpen(false);
    }
  };

  const handleCalendarSelect = (range) => {
    if (range) {
      setDateRange({ from: range.from || null, to: range.to || null });
      setActivePreset(null);
      if (range.from && range.to) {
        setCalOpen(false);
      }
    }
  };

  const clearDates = () => {
    setDateRange({ from: null, to: null });
    setActivePreset('Todo');
  };

  const formatDateLabel = () => {
    if (!dateRange.from && !dateRange.to) return 'Todo el período';
    if (activePreset && activePreset !== 'Todo') return activePreset;
    const fromStr = dateRange.from ? format(dateRange.from, 'dd MMM yyyy', { locale: es }) : '...';
    const toStr = dateRange.to ? format(dateRange.to, 'dd MMM yyyy', { locale: es }) : '...';
    return `${fromStr} — ${toStr}`;
  };

  const exportToCSV = () => {
    if (!equityData?.users) return;
    const headers = ['Usuario', 'Total Asignaciones', 'Días Alta Demanda', 'Score Equidad'];
    const rows = equityData.users.map(u => [
      u.user_name, u.total_assignments, u.high_demand_days, u.equity_score
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `equity_report_${dateRange.from ? format(dateRange.from, 'yyyyMMdd') : 'all'}.csv`;
    a.click();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Reportes y Analítica
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Métricas de equidad y ocupación por período
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={selectedSede} onValueChange={setSelectedSede}>
              <SelectTrigger className="w-[180px] rounded-sm" data-testid="reports-sede-select">
                <SelectValue placeholder="Sede" />
              </SelectTrigger>
              <SelectContent>
                {sedes.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-sm gap-2 min-w-[200px] justify-start font-normal text-left"
                  data-testid="date-range-trigger"
                >
                  <CalendarDays className="w-4 h-4 text-[#002FA7]" />
                  <span className="truncate text-sm">{formatDateLabel()}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="flex">
                  {/* Presets sidebar */}
                  <div className="border-r border-slate-200 p-3 space-y-1 min-w-[140px]">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">Período</p>
                    {PRESET_RANGES.map((p) => (
                      <button
                        key={p.label}
                        onClick={() => handlePreset(p)}
                        className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${
                          activePreset === p.label
                            ? 'bg-[#002FA7] text-white font-medium'
                            : 'hover:bg-slate-100 text-slate-700'
                        }`}
                        data-testid={`preset-${p.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {/* Calendar */}
                  <div className="p-2">
                    <Calendar
                      mode="range"
                      selected={dateRange.from ? { from: dateRange.from, to: dateRange.to } : undefined}
                      onSelect={handleCalendarSelect}
                      numberOfMonths={2}
                      locale={es}
                      defaultMonth={dateRange.from || subMonths(new Date(), 1)}
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {(dateRange.from || dateRange.to) && (
              <Button
                variant="ghost"
                size="icon"
                onClick={clearDates}
                className="h-9 w-9 text-slate-400 hover:text-slate-700"
                data-testid="clear-dates-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            )}

            <Button variant="outline" onClick={exportToCSV} className="rounded-sm" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>

        {/* Active period indicator */}
        {(dateRange.from || dateRange.to) && (
          <div className="flex items-center gap-2 text-sm text-slate-500 bg-blue-50 border border-blue-100 px-4 py-2 rounded-md" data-testid="period-indicator">
            <CalendarDays className="w-4 h-4 text-[#002FA7]" />
            <span>Período: <span className="font-medium text-slate-700">{formatDateLabel()}</span></span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : (
          <>
            {/* Fairness Score */}
            {equityData && (
              <Card className="border border-slate-200" data-testid="fairness-score-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#002FA7]" />
                    Índice de Equidad Global
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div className="text-5xl font-bold text-[#002FA7]" data-testid="fairness-score-value">
                      {equityData.overall_fairness_score}%
                    </div>
                    <div className="flex-1">
                      <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#002FA7] transition-all"
                          style={{ width: `${equityData.overall_fairness_score}%` }}
                        />
                      </div>
                      <p className="text-sm text-slate-500 mt-2">
                        {equityData.overall_fairness_score >= 80 
                          ? 'Excelente distribución de asignaciones'
                          : equityData.overall_fairness_score >= 60
                            ? 'Buena distribución, con margen de mejora'
                            : 'Se recomienda revisar las reglas de asignación'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Equity per User */}
            {equityData?.users?.length > 0 && (
              <Card className="border border-slate-200" data-testid="equity-chart-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Equidad por Usuario</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={equityData.users.slice(0, 15)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis dataKey="user_name" type="category" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="equity_score" fill="#002FA7" name="Score" radius={[0, 2, 2, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Distribution Table */}
            {equityData?.users?.length > 0 && (
              <Card className="border border-slate-200" data-testid="distribution-table-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Detalle de Distribución</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Usuario</th>
                          <th>Total</th>
                          <th>Alta Demanda</th>
                          <th>Lun</th>
                          <th>Mar</th>
                          <th>Mié</th>
                          <th>Jue</th>
                          <th>Vie</th>
                          <th>Equidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {equityData.users.map((u) => (
                          <tr key={u.user_id}>
                            <td className="font-medium">{u.user_name}</td>
                            <td>{u.total_assignments}</td>
                            <td>{u.high_demand_days}</td>
                            <td>{u.day_distribution[0] || 0}</td>
                            <td>{u.day_distribution[1] || 0}</td>
                            <td>{u.day_distribution[2] || 0}</td>
                            <td>{u.day_distribution[3] || 0}</td>
                            <td>{u.day_distribution[4] || 0}</td>
                            <td>
                              <span className={u.equity_score >= 80 ? 'badge-success' : u.equity_score >= 60 ? 'badge-warning' : 'badge-error'}>
                                {u.equity_score}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty state */}
            {equityData && equityData.users?.length === 0 && (
              <Card className="border border-slate-200">
                <CardContent className="flex flex-col items-center justify-center h-48 text-center">
                  <BarChart2 className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay datos de asignación para este período</p>
                  <p className="text-xs text-slate-400 mt-1">Prueba seleccionando un rango de fechas más amplio</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ReportsPage;
